import { useState } from 'react'
import { useTranslation } from '../../i18n'
import { COLORS } from '../../lib/theme'
import { useTeamRoleLabel } from '../../lib/roles'
import { Icon } from '../Icon'
import { PlanHuntAvatar } from './PlanHuntAvatar'
import { StandsMap } from './StandsMap'
import { PlanHuntShuffleAssignModal } from './PlanHuntShuffleAssignModal'
import { moveHunterToStand } from './driveAssignment'
import { isStandPin, isTrailCameraPin } from '../../lib/mapPinTypes'
import { pointInArea } from '../../lib/geo'
import { MIN_RING_POINTS } from '../../lib/polygonUnion'
import { outerRingCount } from '../../lib/mapBorders'
import type { PinTypeCatalog } from '../../lib/pinTypeCatalog'
import type { GeoArea, GeoPt, MapObject } from '../../lib/types'
import type { MapAppearance } from '../../lib/mapAppearance'
import type { PlanHuntDrive, PlanHuntMember, PlanHuntStand } from './types'

/** Inline hunter picker shown beneath a stand row in the panel. */
function InlineAssign({
  stand,
  participants,
  standLabelForHunter,
  onAssign,
}: {
  stand: PlanHuntStand
  participants: PlanHuntMember[]
  standLabelForHunter?: (hunterId: string) => string | null
  onAssign: (id: string | null) => void
}) {
  const { t } = useTranslation('planHuntDrives')
  const roleLabel = useTeamRoleLabel()
  const [query, setQuery] = useState('')
  const filtered = participants.filter((p) => !query || p.name.toLowerCase().includes(query.toLowerCase()))
  return (
    <div
      style={{
        marginTop: 6,
        border: `1px solid ${COLORS.surfaceContainerHighest}`,
        borderRadius: 8,
        background: COLORS.surface,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: 8 }}>
        <input
          className="wh-input"
          placeholder={t('editor.searchHunters')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
      </div>
      <div style={{ maxHeight: 196, overflowY: 'auto', padding: '0 6px 6px' }}>
        {stand.assignedId && (
          <button
            onClick={() => onAssign(null)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              padding: '8px 8px', borderRadius: 6, border: `1px dashed ${COLORS.surfaceContainerHighest}`,
              background: 'transparent', color: COLORS.outline, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', marginBottom: 4, fontFamily: 'inherit', textAlign: 'left',
            }}
          >
            <Icon name="close" size={13} />
            {t('editor.unassignStand')}
          </button>
        )}
        {filtered.map((p) => {
          const active = p.id === stand.assignedId
          // Hunter already on another stand in this drive → picking them moves them.
          const atStand = active ? null : standLabelForHunter?.(p.id) ?? null
          return (
            <button
              key={p.id}
              onClick={() => onAssign(p.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '6px 8px', borderRadius: 6,
                border: `1px solid ${active ? COLORS.primary : 'transparent'}`,
                background: active ? COLORS.primaryContainer : 'transparent',
                cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', marginBottom: 2,
              }}
            >
              <PlanHuntAvatar member={p} size={26} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700 }}>
                  {p.name}
                  {p.you && (
                    <span style={{ fontSize: 10, fontWeight: 500, color: COLORS.outline, marginLeft: 6 }}>{t('editor.you')}</span>
                  )}
                </div>
                {atStand ? (
                  <div style={{ fontSize: 10, color: COLORS.outline }}>{t('editor.alreadyAtStand', { stand: atStand })}</div>
                ) : (
                  p.role && <div style={{ fontSize: 10, color: COLORS.outline }}>{roleLabel(p.role)}</div>
                )}
              </div>
              {active && <Icon name="check" size={15} color={COLORS.primary} />}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function PlanHuntDriveEditor({
  drive,
  index,
  teamPins,
  boundaryAreas = [],
  subAreas = [],
  forbiddenPolygons = [],
  appearance,
  teamBorderColor,
  participants,
  pinTypeCatalog,
  pinSelectionEnabled = false,
  onChange,
  onClose,
}: {
  drive: PlanHuntDrive
  index: number
  teamPins: MapObject[]
  /** Every parcel of the ground, plus its sub-areas and no-hunting zones. */
  boundaryAreas?: readonly GeoArea[]
  forbiddenPolygons?: readonly GeoArea[]
  appearance: MapAppearance
  teamBorderColor?: string
  /** Team-map sub-areas offered as per-drive bases (multi-select). Empty = the
   *  estate sub-area feature is off, so no sub-area section is shown. */
  subAreas?: GeoArea[]
  participants: PlanHuntMember[]
  /** Pin-type catalog used to label the map-pin groups by their localized type
   *  name. Presentation-only — the host builds it and threads it in. */
  pinTypeCatalog?: PinTypeCatalog
  /** Whether the per-drive map-pin multi-select section is shown. Gated by the
   *  same estate feature as the sub-area multi-select. */
  pinSelectionEnabled?: boolean
  onChange: (patch: Partial<PlanHuntDrive>) => void
  onClose: () => void
}) {
  const { t } = useTranslation('planHuntDrives')
  const stands = drive.stands || []
  const pickedIds = new Set(stands.map((s) => s.pinId).filter((id): id is string => id !== null))
  const assignedCount = stands.filter((s) => s.assignedId).length
  const nameMissing = drive.name.trim().length === 0

  // The drive's base is a MULTI-SELECT of sub-areas (mirroring mobile's
  // `subAreaIds`): the drive carries the set, and selecting none means the whole
  // hunting ground. A sub-area is the drive's COVERAGE only — it never decides
  // which pins are visible or pickable. Mobile keeps `subAreaIds`, `pinIds` and
  // `standsAndTowers` fully independent: the pin picker always offers every pin
  // and the preview shows the union of the drive's selected pins and its stands,
  // none of it narrowed to a sub-area. So toggling a sub-area here changes only
  // the highlighted coverage, never the stands on the map or a picked stand.
  const selectedSubAreaIds = new Set(drive.subAreaIds ?? [])
  const selectedPinIds = new Set(drive.pinIds ?? [])

  // Sub-areas may be unnamed on the team map; fall back to their position so
  // two unnamed areas stay distinct (same rule as the hunt's area step).
  const subAreaName = (area: GeoArea) =>
    area.name.trim() || t('editor.unnamedSubArea', { number: subAreas.indexOf(area) + 1 })

  // Only sub-areas with a drawable outline can contain anything; one without
  // rings degrades to "the whole ground" rather than an empty coverage with no
  // explanation.
  const areaHasOutline = (area: GeoArea) =>
    area.rings.filter((r) => r.length >= MIN_RING_POINTS).length > 0
  const selectedAreas = subAreas.filter((a) => selectedSubAreaIds.has(a.id))
  const usableSelectedAreas = selectedAreas.filter(areaHasOutline)

  const pinInArea = (p: MapObject, area: GeoArea) =>
    pointInArea({ lat: p.lat, lon: p.lon }, area.rings, area.outerCount)

  // Every stand/tower pin is drawn and pickable on the map, independent of the
  // selected sub-areas (mobile parity — the pin set never narrows to a sub-area).
  const allStandPins = teamPins.filter(isStandPin)

  // The drive's selected map pins (any type) are drawn on the map too, so the
  // leader SEES what the "Map pins" selection makes visible — the union of the
  // drive's selected pins and its stands, matching mobile's preview. Stand-type
  // selections already render through `allStandPins`; the remaining types are
  // handed to the map as non-interactive display markers (a camera is not a
  // stand, so clicking it must not add a stand).
  const displayPins = teamPins.filter((p) => selectedPinIds.has(p.id) && !isStandPin(p))

  // The auto-fill convenience (closing an empty drive) and its "none means all"
  // hint scope to the drive's COVERAGE — the selected sub-areas' inside stands,
  // or the whole ground when none are selected. This is which stands the drive
  // defaults to, not a visibility filter: the map still shows every stand above.
  // A stored sub-area that matches no offered piece contributes nothing, and if
  // none of the selected pieces are usable we fall back to the whole ground.
  const coverageStandPins =
    usableSelectedAreas.length > 0
      ? allStandPins.filter((p) => usableSelectedAreas.some((a) => pinInArea(p, a)))
      : allStandPins

  // Highlight EVERY selected sub-area's outline, not only when exactly one is
  // picked — the same flat rings + outer-count shape StandsMap resolves through
  // the shared union pipeline (mirrors PlanHuntDriveRow). Before this, selecting
  // a second sub-area dropped the highlight entirely, so the selected area's
  // colour flickered off as soon as more than one was chosen.
  const highlightRings: GeoPt[][] = [
    ...usableSelectedAreas.flatMap((a) => a.rings.slice(0, outerRingCount(a))),
    ...usableSelectedAreas.flatMap((a) => a.rings.slice(outerRingCount(a))),
  ]
  const highlightOuterCount = usableSelectedAreas.reduce((sum, a) => sum + outerRingCount(a), 0)

  const [openAssignId, setOpenAssignId] = useState<string | null>(null)
  const [shuffleOpen, setShuffleOpen] = useState(false)

  const toggleSubArea = (id: string) => {
    const next = new Set(selectedSubAreaIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange({ subAreaIds: [...next] })
  }
  // "Entire hunting ground" clears the selection — the implicit whole-ground
  // state, matching mobile (empty `subAreaIds`).
  const selectEntireGround = () => {
    if (selectedSubAreaIds.size > 0) onChange({ subAreaIds: [] })
  }

  const togglePinId = (id: string) => {
    const next = new Set(selectedPinIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange({ pinIds: [...next] })
  }

  // Click a pin to add the stand; click it again to remove it. Hunter
  // assignment happens in the side panel (per the drive-planning design) — the
  // map is for picking stands, the panel for assigning shooters to them.
  const togglePin = (pinId: string) => {
    if (pickedIds.has(pinId)) {
      onChange({ stands: stands.filter((s) => s.pinId !== pinId) })
    } else {
      onChange({ stands: [...stands, { id: `st-${Date.now()}-${pinId}`, pinId, assignedId: null }] })
    }
  }
  // CLUB-158: assigning a hunter moves them off any other stand in this drive
  // (matches mobile). Pass null to unassign.
  const assignStand = (standId: string, pid: string | null) =>
    onChange({ stands: moveHunterToStand(stands, standId, pid) })
  const removeStand = (standId: string) =>
    onChange({ stands: stands.filter((s) => s.id !== standId) })

  // The stand (pin label) a hunter currently occupies in THIS drive, shown as a
  // picker subtitle so a reassignment reads as a move (CLUB-158, mobile parity).
  const standLabelForHunter = (hunterId: string): string | null => {
    const occupied = stands.find((s) => s.assignedId === hunterId)
    if (!occupied) return null
    return teamPins.find((p) => p.id === occupied.pinId)?.label ?? null
  }

  // Leaving the selection empty means "the whole base": closing the editor
  // with no stands picked puts every stand pin of the base in the drive,
  // unassigned. Picking even one stand opts out of this.
  const closeEditor = () => {
    if (stands.length === 0 && coverageStandPins.length > 0) {
      onChange({
        stands: coverageStandPins.map((p) => ({ id: `st-${Date.now()}-${p.id}`, pinId: p.id, assignedId: null })),
      })
    }
    onClose()
  }

  // ── Map pins multi-select ────────────────────────────────────────────────
  // The sub-area a pin falls in, for the "name (sub-area)" label (mobile
  // parity). Tested against every offered sub-area, not only the selected ones,
  // so a pin reads its location regardless of what the drive currently covers.
  const subAreaForPin = (p: MapObject): GeoArea | null =>
    subAreas.find((a) => areaHasOutline(a) && pinInArea(p, a)) ?? null
  const pinTypeLabel = (type: string) => pinTypeCatalog?.label(type) ?? type
  const pinDisplayName = (p: MapObject) => p.label.trim() || pinTypeLabel(p.type)
  const pinRowLabel = (p: MapObject): string => {
    const sa = subAreaForPin(p)
    const name = pinDisplayName(p)
    return sa ? t('editor.pinWithSubArea', { name, subArea: subAreaName(sa) }) : name
  }
  // A trail camera is drawn on the map but is not a saveable map-pin document,
  // so it is never offered as a selectable pin.
  const pickablePins = teamPins.filter((p) => !isTrailCameraPin(p))
  const pinsByType = new Map<string, MapObject[]>()
  for (const p of pickablePins) {
    const list = pinsByType.get(p.type)
    if (list) list.push(p)
    else pinsByType.set(p.type, [p])
  }
  const pinTypeIds = [...pinsByType.keys()].sort()

  return (
    <div
      onClick={closeEditor}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(20,30,22,0.6)', zIndex: 1300,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="wh-plan-drive-editor"
        style={{
          background: COLORS.surfaceContainerLowest, borderRadius: 14, width: '100%', maxWidth: 1180,
          height: 'min(760px, calc(100vh - 64px))', boxShadow: '0 24px 72px rgba(0,0,0,0.34)',
          display: 'grid', gridTemplateColumns: '1fr 360px', overflow: 'hidden', minHeight: 0,
        }}
      >
        {/* Map */}
        <div style={{ position: 'relative', minHeight: 0 }}>
          {/* The sub-areas go through whole rather than downgraded to bare rings:
              the map needs each one's group id to union them the way the app does,
              and its name to label it. */}
          <StandsMap
            pins={allStandPins}
            displayPins={displayPins}
            selectedIds={pickedIds}
            boundaryAreas={boundaryAreas}
            areaRings={highlightRings}
            areaOuterCount={highlightOuterCount}
            subAreaPolygons={subAreas}
            forbiddenPolygons={forbiddenPolygons}
            appearance={appearance}
            teamBorderColor={teamBorderColor}
            interactive
            onTogglePin={togglePin}
            style={{ width: '100%', height: '100%' }}
          />
          <div
            // onPrimaryFixed, not onSurface: this hint floats on the map behind a
            // hard-coded near-white plate, so the plate looks the same in both
            // themes while onSurface inverts to near-white on the dark palette —
            // which rendered white text on a white card, 1.1:1. The *-fixed
            // tokens carry one value across both palettes, for exactly this.
            style={{
              position: 'absolute', left: 12, top: 12, background: 'rgba(255,255,255,0.95)',
              padding: '8px 12px', borderRadius: 8, fontSize: 12, color: COLORS.onPrimaryFixed,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: 10,
              maxWidth: 'calc(100% - 24px)', pointerEvents: 'none',
            }}
          >
            <Icon name="map" size={14} color={COLORS.primary} />
            <div>
              <div style={{ fontWeight: 600 }}>{t('editor.driveHeading', { number: index + 1, name: drive.name || t('editor.untitled') })}</div>
              <div style={{ color: COLORS.outline, fontSize: 11 }}>
                {t('editor.clickPinHint')}
              </div>
            </div>
          </div>
        </div>

        {/* Panel */}
        <div
          style={{
            display: 'flex', flexDirection: 'column', minHeight: 0,
            borderLeft: `1px solid ${COLORS.surfaceContainerHighest}`, background: COLORS.surfaceContainerLowest,
          }}
        >
          <div
            style={{
              padding: '14px 16px', borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.onSurface }}>{t('editor.editDrive', { number: index + 1 })}</div>
              <div style={{ fontSize: 12, color: COLORS.outline, marginTop: 2 }}>
                {t('editor.standsAssignedSummary', { count: stands.length, assigned: assignedCount })}
              </div>
            </div>
            <button className="wh-btn-icon" onClick={closeEditor} title={t('editor.done')}>
              <Icon name="close" size={16} />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '12px 14px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
              <input
                className="wh-input"
                placeholder={t('editor.driveNamePlaceholder')}
                value={drive.name}
                aria-invalid={nameMissing}
                aria-label={t('editor.driveNameLabel')}
                onChange={(e) => onChange({ name: e.target.value })}
                style={nameMissing ? { borderColor: COLORS.error } : undefined}
              />
              {nameMissing && (
                <span role="alert" style={{ fontSize: 11, color: COLORS.error }}>
                  {t('editor.nameRequired')}
                </span>
              )}
            </div>

            {/* The drive's sub-areas — a multi-select that decides which stand
                pins the map offers. None selected = the whole hunting ground. */}
            {subAreas.length > 0 && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Icon name="map" size={14} color={COLORS.outline} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.onSurface }}>{t('editor.subAreasHeading')}</span>
                </div>
                <div style={{ fontSize: 11, color: COLORS.outline, marginBottom: 8 }}>{t('editor.subAreasHint')}</div>
                <div
                  role="group"
                  aria-label={t('editor.subAreasHeading')}
                  style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}
                >
                  <label
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8,
                      border: `1.5px solid ${selectedSubAreaIds.size === 0 ? COLORS.primary : COLORS.surfaceContainerHighest}`,
                      background: selectedSubAreaIds.size === 0 ? `${COLORS.primary}0d` : COLORS.surface, cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSubAreaIds.size === 0}
                      onChange={selectEntireGround}
                      aria-label={t('editor.entireGround')}
                      className="wh-checkbox"
                    />
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600 }}>{t('editor.entireGround')}</span>
                  </label>
                  {subAreas.map((a) => {
                    const on = selectedSubAreaIds.has(a.id)
                    return (
                      <label
                        key={a.id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8,
                          border: `1.5px solid ${on ? COLORS.primary : COLORS.surfaceContainerHighest}`,
                          background: on ? `${COLORS.primary}0d` : COLORS.surface, cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleSubArea(a.id)}
                          aria-label={subAreaName(a)}
                          className="wh-checkbox"
                        />
                        <span
                          aria-hidden
                          style={{
                            width: 14, height: 14, borderRadius: 4, flexShrink: 0,
                            background: a.color ?? COLORS.outline,
                            border: `1px solid ${COLORS.surfaceContainerHighest}`,
                          }}
                        />
                        <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600 }}>{subAreaName(a)}</span>
                      </label>
                    )
                  })}
                </div>
              </>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Icon name="target" size={14} color={COLORS.outline} />
              <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.onSurface }}>
                {t('editor.stands')}
                {stands.length > 0 && <span style={{ color: COLORS.outline, fontWeight: 500 }}> · {stands.length}</span>}
              </span>
              {stands.length > 0 && (
                <button
                  className="wh-btn wh-btn-outline"
                  style={{ marginLeft: 'auto', height: 28, padding: '0 10px', fontSize: 12 }}
                  onClick={() => setShuffleOpen(true)}
                  title={t('editor.shuffleAssignTitle')}
                >
                  <Icon name="shuffle" size={13} />
                  {t('common.shuffleAssign')}
                </button>
              )}
            </div>

            {stands.length === 0 ? (
              <div
                style={{
                  fontSize: 12, color: COLORS.outline, padding: '14px 12px', textAlign: 'center',
                  border: `1.5px dashed ${COLORS.surfaceContainerHighest}`, borderRadius: 8, background: COLORS.surface,
                }}
              >
                {t('editor.noStandsYet')}
                {coverageStandPins.length > 0 && (
                  <div style={{ marginTop: 6, fontWeight: 600 }}>
                    {t('editor.noneMeansAll', { count: coverageStandPins.length })}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {stands.map((s, i) => {
                  const pin = teamPins.find((p) => p.id === s.pinId)
                  const assignee = participants.find((p) => p.id === s.assignedId)
                  const open = openAssignId === s.id
                  return (
                    <div
                      key={s.id}
                      style={{
                        borderRadius: 8, border: `1px solid ${open ? COLORS.primary : COLORS.surfaceContainerHighest}`,
                        background: COLORS.surfaceContainerLowest, padding: 8,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div
                          style={{
                            width: 22, height: 22, borderRadius: 999, flexShrink: 0, background: COLORS.primary,
                            color: COLORS.onPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 10, fontWeight: 700,
                          }}
                        >
                          {i + 1}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {pin ? pin.label : t('editor.removedPin')}
                          </div>
                          {pin && (
                            <div style={{ fontSize: 11, color: COLORS.outline, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Icon name={pin.type === 'tower' ? 'target' : 'pin'} size={10} />
                              <span style={{ textTransform: 'capitalize' }}>{pin.type}</span>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => setOpenAssignId(open ? null : s.id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px 4px 4px', borderRadius: 999,
                            border: assignee ? `1px solid ${COLORS.surfaceContainerHighest}` : `1px dashed ${COLORS.surfaceContainerHighest}`,
                            background: assignee ? COLORS.surfaceContainerLowest : COLORS.surface, cursor: 'pointer',
                            fontSize: 12, color: assignee ? COLORS.onSurface : COLORS.outline, fontWeight: 600, fontFamily: 'inherit',
                          }}
                          title={assignee ? t('editor.assignedTo', { name: assignee.name }) : t('editor.assignHunter')}
                        >
                          {assignee ? (
                            <>
                              <PlanHuntAvatar member={assignee} size={22} />
                              <span style={{ maxWidth: 96, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {assignee.name.split(' ')[0]}
                              </span>
                            </>
                          ) : (
                            <>
                              <span
                                style={{
                                  width: 22, height: 22, borderRadius: 999, background: 'transparent',
                                  border: `1px dashed ${COLORS.outline}`, color: COLORS.outline,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                              >
                                <Icon name="plus" size={10} />
                              </span>
                              <span>{t('editor.assign')}</span>
                            </>
                          )}
                        </button>
                        <button
                          className="wh-btn-icon"
                          style={{ width: 22, height: 22 }}
                          onClick={() => { setOpenAssignId(null); removeStand(s.id) }}
                          title={t('editor.removeStand')}
                        >
                          <Icon name="trash" size={12} color={COLORS.error} />
                        </button>
                      </div>
                      {open && (
                        <InlineAssign
                          stand={s}
                          participants={participants}
                          standLabelForHunter={standLabelForHunter}
                          onAssign={(pid) => { assignStand(s.id, pid); setOpenAssignId(null) }}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {/* Per-drive map pins — a multi-select of the team's pins grouped by
                pin type. Independent of the stands and sub-areas (mirrors
                mobile's separate `pinIds`); selecting none means no pin
                filtering for this drive. */}
            {pinSelectionEnabled && (
              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Icon name="pin" size={14} color={COLORS.outline} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.onSurface }}>
                    {t('editor.pinsHeading')}
                    {selectedPinIds.size > 0 && <span style={{ color: COLORS.outline, fontWeight: 500 }}> · {selectedPinIds.size}</span>}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: COLORS.outline, marginBottom: 8 }}>{t('editor.pinsHint')}</div>
                {pinTypeIds.length === 0 ? (
                  <div
                    style={{
                      fontSize: 12, color: COLORS.outline, padding: '14px 12px', textAlign: 'center',
                      border: `1.5px dashed ${COLORS.surfaceContainerHighest}`, borderRadius: 8, background: COLORS.surface,
                    }}
                  >
                    {t('editor.pinsEmpty')}
                  </div>
                ) : (
                  <div
                    role="group"
                    aria-label={t('editor.pinsHeading')}
                    style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
                  >
                    {pinTypeIds.map((typeId) => (
                      <div key={typeId}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.outline, marginBottom: 4 }}>
                          {pinTypeLabel(typeId)}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {pinsByType.get(typeId)!.map((p) => {
                            const on = selectedPinIds.has(p.id)
                            return (
                              <label
                                key={p.id}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 8,
                                  border: `1.5px solid ${on ? COLORS.primary : COLORS.surfaceContainerHighest}`,
                                  background: on ? `${COLORS.primary}0d` : COLORS.surface, cursor: 'pointer',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={on}
                                  onChange={() => togglePinId(p.id)}
                                  aria-label={pinRowLabel(p)}
                                  className="wh-checkbox"
                                />
                                <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500 }}>{pinRowLabel(p)}</span>
                              </label>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            style={{
              padding: '12px 14px', borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
              display: 'flex', gap: 8, background: COLORS.surface,
            }}
          >
            <button className="wh-btn wh-btn-primary" style={{ marginLeft: 'auto' }} onClick={closeEditor}>
              {t('editor.done')}
            </button>
          </div>
        </div>
      </div>

      {shuffleOpen && (
        <PlanHuntShuffleAssignModal
          stands={stands}
          teamPins={teamPins}
          participants={participants}
          onCancel={() => setShuffleOpen(false)}
          onApply={(newStands) => { onChange({ stands: newStands }); setShuffleOpen(false) }}
        />
      )}
    </div>
  )
}
